import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-hospital-line',
    title: 'Conformidade com ANVISA e Vigilância Sanitária',
    description: 'Materiais de fácil higienização com álcool 70%, superfícies lisas sem poros e acabamentos que resistem a desinfetantes. Sua clínica em conformidade com as normas sanitárias.',
  },
  {
    icon: 'ri-armchair-line',
    title: 'Mochos ergonômicos para salas de coleta',
    description: 'Mochos com regulagem de altura, assento anatômico e base com rodízios silenciosos. O profissional de saúde trabalha com conforto e precisão durante os procedimentos.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito da clínica',
    description: 'Recepção, consultórios, sala de coleta e corredor projetados em 3D antes de fabricar. Você aprova o layout completo e garante que tudo vai caber e funcionar.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem sem interromper atendimentos',
    description: 'Nossa equipe própria instala no horário que sua clínica preferir — fins de semana, feriados ou madrugada. Seus pacientes não são afetados.',
  },
];

const blocks = [
  {
    title: 'Mobiliário Técnico que Une Elegância e Conformidade com as Normas de Saúde',
    text: [
      'Uma clínica médica ou laboratório de análises não pode ter mobiliário que acumula sujeira, descasca com álcool 70% ou apresenta frestas onde bactérias se proliferam. Nosso mobiliário para saúde utiliza MDF revestido em laminado melamínico de alta pressão (HPL), que resiste a álcool, água sanitária e produtos de limpeza hospitalar sem perder o acabamento.',
      'As bordas são seladas com fita de PVC de 2mm que impede a penetração de umidade. Os puxadores são embutidos ou em inox escovado, sem reentrâncias que acumulam resíduos. E as superfícies de trabalho — balcões de coleta, bancadas de laboratório, mesas de exame — são revestidas em material liso e não poroso, de fácil desinfecção. Elegância e conformidade sanitária não são opostos — são o padrão da Home Office Premium.',
    ],
    img: '/media/ia/0317.webp',
    reverse: false,
  },
  {
    title: 'Poltronas de Espera Confortáveis e Recepção que Transmite Confiança',
    text: [
      'Pacientes ansiosos precisam de um ambiente que acalme. Nossas poltronas de espera têm assento em espuma D33 com courvin antimicrobiano que resiste a álcool e não descasca, estrutura em aço com capacidade de 180kg, e braços que facilitam a sentada e levantada de idosos e gestantes. Disponíveis em módulos de 2 e 3 lugares para otimizar o espaço da sala de espera.',
      'O balcão de recepção é projetado com tampo em granito ou laminado HPL, espaço interno para computador, impressora e organização de prontuários, e frente em MDF com acabamento que pode receber o logo da clínica em relevo ou adesivo. A primeira impressão do paciente começa aqui — e ela precisa transmitir profissionalismo e cuidado.',
    ],
    img: '/media/ia/0318.webp',
    reverse: true,
  },
  {
    title: 'Consultórios, Salas de Coleta e Laboratórios Completos',
    text: [
      'Para consultórios médicos e odontológicos, oferecemos mesas de exame com regulagem de altura elétrica, armários para medicamentos com chave e ventilação, carros de curativo com gavetas organizadas e rodízios silenciosos, e cadeiras ergonômicas para o profissional com mecanismo de balanço e apoio lombar ajustável.',
      'Para laboratórios de análises clínicas, fazemos bancadas de coleta com tampo em granito ou inox, armários para reagentes com ventilação e trava de segurança, e divisórias que criam boxes de coleta individuais com privacidade para o paciente. Tudo projetado para atender às normas da ANVISA e da Vigilância Sanitária estadual.',
    ],
    img: '/media/ia/0319.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0320.webp',
    alt: 'Balcão de recepção para clínica',
  },
  {
    src: '/media/ia/0321.webp',
    alt: 'Mocho ergonômico para sala de coleta',
  },
  {
    src: '/media/ia/0322.webp',
    alt: 'Poltronas antimicrobianas para espera',
  },
  {
    src: '/media/ia/0323.webp',
    alt: 'Bancada para laboratório de análises',
  },
];

export default function ClinicasPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Clínica Médica Caruaru PE | ANVISA – Home Office Premium',
    description: 'Mobiliário para clínicas médicas e laboratórios em Caruaru PE. Conformidade ANVISA, materiais higienizáveis, mochos ergonômicos e projeto 3D gratuito. Atendemos clínicas no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário clínica médica Recife, móveis laboratório Petrolina, mocho ergonômico Garanhuns, poltrona espera clínica Arcoverde, recepção clínica Sertão PE, mobiliário ANVISA Pernambuco, consultório médico Agreste, mobiliário clínica Nordeste, bancada coleta Recife, mobiliário laboratório Petrolina, poltrona clínica Garanhuns, mobiliário saúde Caruaru, consultório médico Bezerros',
    path: '/clinicas',
  });

  useSchemaLD({
    id: 'schema-clinicas',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Clínicas Médicas e Laboratórios | Caruaru PE',
      description: 'Mobiliário técnico para clínicas médicas e laboratórios: conformidade ANVISA, materiais higienizáveis, mochos ergonômicos e projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/clinicas`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Clínicas Médicas e Laboratórios',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0324.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Clínicas Médicas · Laboratórios · Saúde
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário técnico que une elegância e conformidade com as normas de saúde.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Materiais higienizáveis com álcool 70%, poltronas antimicrobianas para pacientes e mochos ergonômicos para salas de coleta. Sua clínica em conformidade com a <strong className="text-white">ANVISA</strong> — e com visual de alto padrão.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20M%C3%A9dicas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que clínicas de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Saúde</span> Projetados com Precisão
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20M%C3%A9dicas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Projeto 3D completo da sua clínica, antes de comprar qualquer móvel.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Recepção, consultórios, sala de coleta e corredor projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem pela nossa equipe, sem interromper seus atendimentos.
          </p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20M%C3%A9dicas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Mobiliário para <span className="text-[#E8610A]">Clínicas e Laboratórios</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20M%C3%A9dicas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20M%C3%A9dicas%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
